import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ADMIN_ME_QUERY_KEY } from "@/lib/admin-queries";
import { AdminApiError, adminApi } from "@/lib/api";

function messageForError(error: unknown): string {
  if (error instanceof AdminApiError) {
    if (error.code === "invalid_credentials") return "Nieprawidłowe hasło.";
    if (error.code === "login_unavailable") {
      return "Logowanie hasłem nie jest włączone dla tego środowiska.";
    }
  }
  return "Logowanie nie powiodło się. Spróbuj ponownie.";
}

// Shown when GET /admin/me is unauthenticated (password mode). On success the
// admin-session cookie is set and we refetch /admin/me to reveal the console.
export function LoginScreen() {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const mutation = useMutation({
    mutationFn: (pw: string) => adminApi.login(pw),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMIN_ME_QUERY_KEY }),
  });

  return (
    <div className="flex min-h-dvh items-center justify-center bg-paper px-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <div className="mb-1 flex items-center gap-2">
            <span className="font-display text-lg font-semibold tracking-tight">pufimi</span>
            <span className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              panel
            </span>
          </div>
          <CardTitle>Zaloguj się</CardTitle>
          <CardDescription>Panel operacyjny — dostęp tylko dla zespołu.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              mutation.mutate(password);
            }}
          >
            <div className="space-y-1.5">
              <label htmlFor="admin-password" className="text-sm font-medium">
                Hasło
              </label>
              <Input
                id="admin-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {mutation.isError && (
              <p className="text-sm text-destructive" role="alert">
                {messageForError(mutation.error)}
              </p>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={mutation.isPending || password.length === 0}
            >
              {mutation.isPending ? "Logowanie…" : "Zaloguj się"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
